<template>
  <div>
    <div class="text-h5 mb-5">
      Work In Progress Checks
    </div>
    <ListActiveProcessCheckSheets />
    <div class="text-h5 mt-5 mb-5">
      <v-btn color="success" v-if="selected.length > 0" @click="batch">
        <v-icon left>{{ mdiCheckCircleOutline }}</v-icon> Batch Items
      </v-btn> Factory Items
    </div>
    <v-data-table
      dense
      :headers="headers"
      :items="factoryItems"
      class="elevation-1"
      :loading="isLoading"
      :expanded.sync="expanded"
      :items-per-page="50"
      v-model="selected"
      show-select
      show-expand
      item-key="id"
    >
      <template v-slot:expanded-item="{ headers, item }">
        <td v-for="(header, i) in headers" :key="`expanded-${item.id}-${header.value}`">
          <v-icon v-if="i === 0" class="ml-2">{{ mdiChevronRight }}</v-icon>
          <div class="expanded-item-container" v-else-if="i === 7">
            <div
              class="expanded-item"
              :class="{ 'expanded-item-child': childIndex > 0 }"
              block v-for="(child, childIndex) in item.children"
              :key="child.id">
              {{ child.stockType }}
              <span v-if="child.splitPercentage">({{ child.splitPercentage.toFixed(2) }}%)</span>
            </div>
          </div>
          <!-- <div class="expanded-item-container" v-else-if="i === 8">
            <div :class="{ 'expanded-item-child': childIndex > 0 }"
              block v-for="(child, childIndex) in item.children"
              :key="child.id">
              <FactoryItemChecks :item="child" :index="index" />
            </div>
          </div> -->
          <div class="expanded-item-container" v-else-if="i === 8">
            <div class="expanded-item"
              block v-for="(child, childIndex) in item.children"
              :key="child.id">
              <FactoryItemActions :item="child" :index="`child-${childIndex}`" @refresh="refresh" />
            </div>
          </div>
          <div class="expanded-item-container" v-else>
            <div
              class="expanded-item"
              :class="{ 'expanded-item-child': childIndex > 0 }"
              block v-for="(child, childIndex) in item.children"
              :key="child.id">
              <v-checkbox
                v-if="i === 1"
                @change="toggleExpandedSelected($event, child)"
              />
              {{ child[header.value] }}&nbsp;
            </div>
          </div>
        </td>
      </template>
      <template v-slot:item.data-table-expand="{ expand, isExpanded, item, index }">
        <div v-if="item.state === 'Split' || item.state === 'Processing-Batched'">
          <v-btn
            class="btn btn-icon"
            icon
            @click="expand(!isExpanded)"
            :data-cy="`ListFactoryItem-button-expand-${index}`"
          >
            <v-icon v-if="isExpanded">
              {{ mdiChevronUp }}
            </v-icon>
            <v-icon v-else>
              {{ mdiChevronDown }}
            </v-icon>
          </v-btn>
        </div>
      </template>
      <template v-slot:item.checks="{ item, index }">
        <FactoryItemChecks :item="item" :index="index" />
      </template>
      <template v-slot:item.actions="{ item, index }">
        <FactoryItemActions :item="item" :index="`${index}`" @refresh="refresh" />
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import {
  mdiArrowRight,
  mdiChevronDown,
  mdiChevronRight,
  mdiChevronUp,
  mdiCheckCircleOutline,
  mdiFileDocument,
  mdiCheckAll,
  mdiCallSplit,
} from '@mdi/js';
import loading from '@codehq/aurora-app-core/src/mixins/loading';
import formatting from '../mixins/formatting';
import FactoryItemActions from '../components/FactoryItemActions.vue';
import FactoryItemChecks from '../components/FactoryItemChecks.vue';
import ListActiveProcessCheckSheets from '../components/ListActiveProcessCheckSheets.vue';

export default {
  components: { FactoryItemActions, FactoryItemChecks, ListActiveProcessCheckSheets },
  name: 'ListFactoryItem',
  module: 'FactoryItem',
  mixins: [formatting, loading],
  data() {
    return {
      /**
      * icons
      */
      mdiArrowRight,
      mdiChevronDown,
      mdiChevronRight,
      mdiChevronUp,
      mdiCheckCircleOutline,
      mdiFileDocument,
      mdiCheckAll,
      mdiCallSplit,
      /**
      * variables
      */
      expanded: [],
      selected: [],
    };
  },
  computed: {
    /** Vuex states
     */
    ...mapState('stockItems', ['processingStockItems']),
    ...mapState('stockItemLinks', ['stockItemLinks']),
    /**
    * Get table column headers
    */
    headers() {
      return [
        {
          text: this.$$t('productionCode'),
          value: 'productionCode',
        },
        {
          text: this.$$t('receivingDate'),
          value: 'receivingDate',
        },
        {
          text: this.$$t('productionDate'),
          value: 'productionDate',
        },
        {
          text: this.$$t('sizeRange'),
          value: 'sizeRange',
        },
        {
          text: this.$$t('receivedMass'),
          value: 'receivedMass',
        },
        {
          text: this.$$t('processingMass'),
          value: 'processingMass',
        },
        {
          text: this.$$t('stockType'),
          value: 'stockType',
        },
        {
          text: '',
          value: 'actions',
        },
      ];
    },
    factoryItems() {
      const { stockItemLinks } = this;
      let { processingStockItems } = this;
      if (!processingStockItems) {
        processingStockItems = [];
      }
      const factoryItems = this.mapFactoryItems(processingStockItems);
      const factoryItemsFiltered = factoryItems
        .filter((f) => f.state !== 'Processing-Split' && f.state !== 'Batched');
      console.debug(factoryItemsFiltered);
      return factoryItemsFiltered
        .map((f) => {
          const result = { ...f };
          // get children
          const childrenLinks = stockItemLinks
            .filter((l) => l.parentStockItemId === f.id)
            .map((l) => l.childStockItemId);

          const childrenItems = processingStockItems
            .filter((i) => childrenLinks.includes(i.id));
          result.children = this.mapFactoryItems(childrenItems);
          return result;
        });
    },
  },
  async mounted() {
    await this.refresh();
  },
  methods: {
    /** Vuex methods
     */
    ...mapActions('stockItems', ['LOAD_processingStockItems', 'BATCH_stockItems']),
    ...mapActions('stockItemLinks', ['LOAD_stockItemLinks']),
    async batch() {
      const ids = this.selected.map((s) => s.id);
      console.debug(this.selected, ids);
      this.isLoading = true;
      try {
        await this.BATCH_stockItems({
          stockItemIds: ids,
        });
      } catch (error) {
        console.error(error);
        // eslint-disable-next-line no-alert
        window.alert('There was an error processing the selected items');
      } finally {
        this.isLoading = false;
      }
    },
    childrenFilter(child) {
      return child.value !== 'checks' && child.value !== 'actions';
    },
    toggleExpandedSelected($event, child) {
      console.debug({ $event, child });
      if ($event) {
        this.selected.push(child);
      } else {
        this.selected = this.selected.filter((e) => e !== child);
      }
    },
    mapFactoryItems(processingStockItems) {
      return processingStockItems.map((s) => {
        const result = { ...s };
        const {
          deliveryNote, productionDate,
        } = result;
        // eslint-disable-next-line max-len
        result.receivedMass = s.receivedMass?.toFixed(3) ?? 0;
        result.processingMass = s.processingMass?.toFixed(3) ?? 0;
        result.productionCode = this.formatProductionCode(result);
        result.receivingDate = this.formatDate(deliveryNote?.receivingDate ?? '');
        result.productionDate = this.formatDate(productionDate);
        result.sizeRange = `${s.sizeMin} - ${s.sizeMax}`;
        return result;
      });
    },
    async refresh() {
      this.isLoading = true;
      await Promise.all([
        (async () => {
          await this.LOAD_processingStockItems();
        })(),
        (async () => {
          await this.LOAD_stockItemLinks();
        })(),
      ]);
      this.isLoading = false;
    },
  },
};
</script>

<docs>
# FactoryItem example

'''js
<FactoryItem />
'''
</docs>

<style lang="scss">
.expanded-item {
  padding-top: 20px;
  padding-bottom: 20px;
}
.expanded-item-child {
  border-top: 1px solid var(--v-primary-base);
}
.expanded-item-container {
  /* height: 100%; */
}
</style>
